import {
    useEffect, useState, useRef, useCallback
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    useContext
} from 'react';
import {
    FileSelection
} from './FileList';
import {
    getFileSystem
} from './FileSystemHelper';
import {
    Context
} from 'shared/context/GlobalState';
import {
    LoaderSpinner
} from 'shared/components/LoaderSpinner';
import {
    Button
} from 'shared/components/ui/button';

export function FileSelectionModal({
    isOpen, setIsOpen, collection_id, onFileConfirm, fileSystem = null, setFileSystem = null, multiple = false
}) {
    const {
        collections, fetchCollections
    } = useContext(SubOrgContext);
    const {
        addNewNotifcation
    } = useContext(Context);
    const collectionDetails = collections?.find((coll) => coll._id === collection_id);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isFetchingFiles, setIsFetchingFiles] = useState(false);
    const {
        oragID, subOragID
    } = useParams();
    const [localFileSystem, setLocalFileSystem] = useState([]);
    const finalFileSystem = fileSystem ?? localFileSystem;
    const finalSetFileSystem = setFileSystem ?? setLocalFileSystem;
    const previousCollectionId = useRef(collection_id);

    const initializeFileSystem = async () => {
        const maxRetries = 5;
        let retryCount = 0;
        let success = false;

        while (retryCount < maxRetries && !success) {
            try {
                finalSetFileSystem([]);

                setIsFetchingFiles(true);
                const files = await getFileSystem(collections, oragID, subOragID, collection_id);
                finalSetFileSystem(files);
                success = true; // If successful, exit the loop
                // addNewNotifcation("Files fetched successfully.", "success");
            } catch (error) {
                retryCount++;
                console.error(`Attempt ${retryCount} failed:`, error);

                if (retryCount === maxRetries) {
                    console.error('Max retries reached. Could not retrieve the files.');
                    addNewNotifcation('Could not retrieve the files after multiple attempts.', 'danger');
                } else { /* empty */

                }
            } finally {
                setIsFetchingFiles(false);
            }
        }
    };

    const handleConfirmFile = () => {
        onFileConfirm(selectedFiles);
        setIsOpen(false);
    };

    const onFileClick = useCallback((fileList) => {
        setSelectedFiles(fileList);
    }, []);

    useEffect(() => {
        // Only fetch files if collection_id exists and has changed
        if (collection_id &&
            collection_id !== '' &&
            previousCollectionId.current !== collection_id) {
            previousCollectionId.current = collection_id;
            initializeFileSystem();
        }
    }, [collection_id]);

    useEffect(() => {
        if (isOpen) {
            fetchCollections();
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="sm:max-w-6xl select-none h-fit max-h-[650px] min-h-[300px]">
                <DialogHeader>
                    <DialogTitle>Select a file from the collection: {collectionDetails?.name || 'undefined'}</DialogTitle>
                    <DialogDescription>
                        Get selected file details from the collection.
                    </DialogDescription>
                </DialogHeader>

                {isFetchingFiles ?
                    <div className="flex flex-row mx-auto gap-2 h-fit p-2 text-gray-400">
                        <span>Fetching Files</span>
                        <LoaderSpinner className='w-6 h-6' />
                    </div>
                    : !finalFileSystem ?
                        <div className="flex flex-row mx-auto gap-2 h-fit p-2 text-gray-400">
                            <span>No Files</span>
                        </div>
                        : <FileSelection fileSystem={finalFileSystem} onFileClick={onFileClick} multiple={multiple} />}

                <DialogFooter>
                    <Button variant="outline" onClick={() => setIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button disabled={selectedFiles.length === 0} onClick={handleConfirmFile}>
                        Select
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}